/** AppLayout — three-column shell (sidebar + main + optional compass). */

import { type ReactNode, useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Drawer } from "antd";
import { MenuOutlined } from "@ant-design/icons";
import Sidebar from "./Sidebar";
import GlobalTopBar from "./GlobalTopBar";
import { TopBarProvider } from "./TopBarContext";
import styles from "../../styles/layout.module.css";

/** Mobile breakpoint — matches CSS @media (max-width: 767px). */
const MOBILE_QUERY = "(max-width: 767px)";

/** Hook that tracks whether the viewport is mobile-sized. */
function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia(MOBILE_QUERY).matches,
  );

  useEffect(() => {
    const mql = window.matchMedia(MOBILE_QUERY);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);

  return isMobile;
}

interface AppLayoutProps {
  /** Page content rendered in the main column. */
  children: ReactNode;
  /** Whether to show the right-side compass panel. */
  showCompass?: boolean;
  /** Custom content for the compass panel. Falls back to nothing. */
  compassContent?: ReactNode;
  /** Whether the desktop sidebar is shown. Mobile drawer is unaffected and
      remains available for navigation. */
  showSidebar?: boolean;
  /** Whether the global top bar is rendered above main content. Pages that
      carry their own toolbar (e.g. WorkbenchDetailPage) pass false to avoid
      duplicate chrome. TopBarProvider still wraps children so pages can
      keep calling useTopBarLeft / useTopBarActions without error. */
  showTopBar?: boolean;
  /** Whether the sidebar is collapsed. */
  sidebarCollapsed?: boolean;
  /** Whether the compass panel is collapsed. */
  compassCollapsed?: boolean;
  /** Main column scroll behavior.
   *  - "auto" (default): main scrolls vertically when content overflows. Standard list/page behavior.
   *  - "hidden": main becomes flex column and never scrolls; children must
   *    manage their own internal scroll region. Used by full-height workspaces
   *    like the conversation detail page (header + scrollable thread + composer). */
  mainScroll?: "auto" | "hidden";
}

export default function AppLayout({
  children,
  showCompass = false,
  compassContent,
  showSidebar = true,
  showTopBar = true,
  sidebarCollapsed = false,
  compassCollapsed = false,
  mainScroll = "auto",
}: AppLayoutProps) {
  const location = useLocation();
  const isMobile = useIsMobile();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Auto-close mobile drawer on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleMobileMenuToggle = useCallback(() => {
    setMobileMenuOpen((prev) => !prev);
  }, []);

  const handleMobileMenuClose = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  const sidebarElement = (
    <Sidebar collapsed={sidebarCollapsed} onNavigate={handleMobileMenuClose} />
  );

  return (
    <div className={styles.app} data-testid="app-layout">
      {/* Desktop sidebar — hidden on mobile via CSS, or when showSidebar=false
          (e.g. full-bleed workbench detail page that needs the width). */}
      {!isMobile && showSidebar && sidebarElement}

      {/* Mobile top bar with hamburger */}
      {isMobile && (
        <div className={styles.mobileTopBar} data-testid="mobile-top-bar">
          <button
            className={styles.hamburgerBtn}
            onClick={handleMobileMenuToggle}
            aria-label="打开导航菜单"
            type="button"
          >
            <MenuOutlined />
          </button>
        </div>
      )}

      {/* Mobile drawer sidebar */}
      {isMobile && (
        <Drawer
          placement="left"
          open={mobileMenuOpen}
          onClose={handleMobileMenuClose}
          size={256}
          closable={false}
          styles={{
            body: { padding: 0, background: "transparent" },
          }}
          rootClassName={styles.mobileDrawer}
          destroyOnHidden={false}
        >
          {sidebarElement}
        </Drawer>
      )}

      <main
        className={`${styles.main} ${mainScroll === "hidden" ? styles.mainScrollHidden : ""}`}
        role="main"
        data-testid="app-layout-main"
      >
        <TopBarProvider>
          {showTopBar && <GlobalTopBar />}
          {children}
        </TopBarProvider>
      </main>

      {showCompass && (
        <aside
          className={`${styles.compass} ${compassCollapsed ? styles.compassCollapsed : ""}`}
          role="complementary"
          aria-label="待办与事项"
        >
          {compassContent}
        </aside>
      )}
    </div>
  );
}
